import { TConfiguredTracker } from "@/tracking/createTrackerConfig";
import { TBillingInterval } from "@/types/app";
import { toMajorUnits } from "@/utils/toMajorUnits";
import { useIntercom } from "react-use-intercom";
import { DashboardService } from "../service.dashboard";
import { useIntervalChangeMutation } from "@/components/shared/interval-switch/use-interval-change-mutation";

interface ConfirmIntervalSwitchOptions<TEvent extends string> {
  /** The host page's service tracker, which stamps its own page props */
  track: TConfiguredTracker<TEvent>;
  isV1?: boolean;
  onSuccess?: () => void;
}

interface ConfirmIntervalSwitchArgs<TEvent extends string> {
  /** Tracked once the switch succeeds */
  event: TEvent;
  targetInterval: TBillingInterval;
  /** In the minor unit `currency` arrives in */
  amountDueMinor: number;
  currency: string;
  promoCode?: string;
  isReferralCreditUsed?: boolean;
  referralCreditAmount?: number;
  /** Extra event props; the switch's own props win on a clash */
  properties?: Record<string, unknown>;
}

/**
 * Switches the plan's billing interval and tracks the confirmation. Failures
 * open Intercom; `confirmSwitch` also rejects on them so a caller can react.
 */
export function useConfirmIntervalSwitch<TEvent extends string>({
  track,
  isV1,
  onSuccess,
}: ConfirmIntervalSwitchOptions<TEvent>) {
  const { showNewMessage } = useIntercom();

  const intervalChangeMutation = useIntervalChangeMutation({
    isV1,
    onSuccess,
    onError: () => {
      showNewMessage(
        DashboardService.Content.switchIntervalCard.supportMessage,
      );
    },
  });

  async function confirmSwitch({
    event,
    targetInterval,
    amountDueMinor,
    currency,
    promoCode,
    isReferralCreditUsed = false,
    referralCreditAmount = 0,
    properties,
  }: ConfirmIntervalSwitchArgs<TEvent>) {
    // The mutation resolves on a failed or redirecting response too
    const response = await intervalChangeMutation.mutateAsync({
      targetInterval,
      promoCode,
      isReferralCreditUsed,
    });
    if (!response.status) {
      throw new Error(response.error?.message ?? "Interval change failed");
    }

    track(event, {
      ...properties,
      target_interval: targetInterval,
      amount_due: toMajorUnits(amountDueMinor, currency),
      currency,
      has_promo_code: !!promoCode,
      promo_codes: promoCode ? [promoCode] : [],
      is_referral_credit_used: isReferralCreditUsed,
      referral_credit_amount: isReferralCreditUsed ? referralCreditAmount : 0,
    });
  }

  return {
    confirmSwitch,
    isPending: intervalChangeMutation.isPending,
    isError: intervalChangeMutation.isError,
  };
}
